/**
 * Command history + issuance for ONE device, polled while anything is
 * non-terminal. No business rules live here — tiering, phase gating and copy
 * are the drawer's job; this hook only talks to the service and tells the
 * truth about what it said.
 *
 * `isTerminalState` from `@platform/shared-types` is the ONLY lifecycle
 * authority this hook consults — it never re-derives "is this command done"
 * itself.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FleetActionId, FleetCommand } from '@platform/shared-types';
import { isTerminalState } from '@platform/shared-types';
import { fleetSupportApi } from '../../../services/fleetSupportApi';

const POLL_MS = 3000;
const LIST_LIMIT = 50;

export interface UseDeviceCommands {
  commands: FleetCommand[];
  /** True while the initial load has not yet returned. */
  loading: boolean;
  /** Set when the most recent read failed; cleared on the next success. */
  error: string | null;
  /** True while a background poll (not the initial load) is in flight. */
  refreshing: boolean;
  issue: (action: FleetActionId, body?: { params?: Record<string, unknown>; reason?: string; codeId?: string; code?: string }) => Promise<FleetCommand>;
  cancel: (commandId: string) => Promise<void>;
}

export function useDeviceCommands(deviceId: string | undefined, deviceType: string | undefined): UseDeviceCommands {
  const [commands, setCommands] = useState<FleetCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const liveRef = useRef(true);

  const hasNonTerminal = (rows: FleetCommand[]) => rows.some((c) => !isTerminalState(c.state));

  const load = useCallback(async (background: boolean) => {
    if (!deviceId) return;
    if (background) setRefreshing(true);
    try {
      const res = await fleetSupportApi.listCommands(deviceId, LIST_LIMIT);
      if (!liveRef.current) return;
      setCommands(res.commands || []);
      setError(null);
    } catch (e: any) {
      if (!liveRef.current) return;
      setError(e?.message || 'Could not load command history');
    } finally {
      if (!liveRef.current) return;
      if (background) setRefreshing(false);
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    liveRef.current = true;
    setLoading(true);
    void load(false);
    return () => { liveRef.current = false; if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [load]);

  // Poll every 3s ONLY while something is non-terminal; stop the moment
  // nothing is left in flight, and never poll before the initial load lands.
  useEffect(() => {
    if (loading) return;
    if (!hasNonTerminal(commands)) return;
    timerRef.current = window.setTimeout(() => { void load(true); }, POLL_MS);
    return () => { if (timerRef.current) window.clearTimeout(timerRef.current); };
  }, [commands, loading, load]);

  const issue: UseDeviceCommands['issue'] = useCallback(async (action, body) => {
    if (!deviceId || !deviceType) throw new Error('No device selected');
    const created = await fleetSupportApi.createCommand({
      deviceId,
      deviceType,
      action,
      params: body?.params,
      reason: body?.reason,
      codeId: body?.codeId,
      code: body?.code,
    });
    // Push in with the SERVER'S state (queued/sent) — never an invented
    // 'done'. A queued command against an offline device must keep reading
    // "queued", not a fabricated success.
    setCommands((prev) => [created, ...prev]);
    return created;
  }, [deviceId, deviceType]);

  const cancel = useCallback(async (commandId: string) => {
    if (!deviceId) throw new Error('No device selected');
    const updated = await fleetSupportApi.cancelCommand(commandId, deviceId);
    setCommands((prev) => prev.map((c) => (c.commandId === commandId ? updated : c)));
  }, [deviceId]);

  return { commands, loading, error, refreshing, issue, cancel };
}
